import { mdxFromMarkdown } from "mdast-util-mdx";
import "mdast-util-mdx-jsx";
import { factorySpace } from "micromark-factory-space";
import { markdownLineEnding } from "micromark-util-character";
import { codes } from "micromark-util-symbol/codes.js";
import { types } from "micromark-util-symbol/types.js";
import { Parser } from "acorn";
import acornJsx from "acorn-jsx";
import { mdxJsx } from "micromark-extension-mdx-jsx";
import { mdxMd } from "micromark-extension-mdx-md";
import { combineExtensions } from "micromark-util-combine-extensions";
import remarkParse from "remark-parse";
import { unified } from "unified";
import { defaultSchema } from "hast-util-sanitize";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import rehypeStringify from "rehype-stringify";
import remarkRehype from "remark-rehype";
import { marked } from "marked";
function remarkComment(options) {
  const data = this.data();
  const add = (field, value) => (data[field] ? data[field] : data[field] = []).push(value);
  add("micromarkExtensions", comment);
  add("htmlExtensions", commentHtml);
  add("fromMarkdownExtensions", commentFromMarkdown(options));
  add("toMarkdownExtensions", commentToMarkdown);
}
const comment = {
  flow: { [60]: { tokenize, concrete: true } },
  text: { [60]: { tokenize } }
};
const commentHtml = {
  enter: {
    comment() {
      this.buffer();
    }
  },
  exit: {
    comment() {
      this.resume();
      this.setData("slurpOneLineEnding", true);
    }
  }
};
const commentFromMarkdown = function(options) {
  return {
    canContainEols: ["comment"],
    enter: {
      comment(token) {
        this.buffer();
      }
    },
    exit: {
      comment(token) {
        const text = this.resume();
        if (options == null ? void 0 : options.ast) {
          this.enter(
            {
              type: "comment",
              value: "",
              commentValue: text.slice(0, -2)
            },
            token
          );
          this.exit(token);
        }
      }
    }
  };
};
const commentToMarkdown = {
  handlers: {
    comment(node) {
      return `<!--${node.commentValue.replace(/(--)>/g, "--\\>")}-->`;
    }
  }
};
function tokenize(effects, ok, nok) {
  return start;
  function start(code) {
    effects.enter("comment");
    effects.consume(code);
    return open;
  }
  function open(code) {
    if (code === codes.exclamationMark) {
      effects.consume(code);
      return declarationOpen;
    }
    return nok(code);
  }
  function declarationOpen(code) {
    if (code === codes.dash) {
      effects.consume(code);
      return commentOpen;
    }
    return nok(code);
  }
  function commentOpen(code) {
    if (code === codes.dash) {
      effects.consume(code);
      return commentStart;
    }
    return nok(code);
  }
  function commentStart(code) {
    if (code === codes.greaterThan) {
      return nok(code);
    }
    if (markdownLineEnding(code)) {
      return atLineEnding(code);
    }
    effects.enter(types.data);
    if (code === codes.dash) {
      effects.consume(code);
      return commentStartDash;
    }
    return comment2(code);
  }
  function commentStartDash(code) {
    if (code === codes.greaterThan) {
      return nok(code);
    }
    return comment2(code);
  }
  function comment2(code) {
    if (code === codes.eof) {
      return nok(code);
    }
    if (code === codes.dash) {
      effects.consume(code);
      return commentClose;
    }
    if (markdownLineEnding(code)) {
      effects.exit(types.data);
      return atLineEnding(code);
    }
    effects.consume(code);
    return comment2;
  }
  function atLineEnding(code) {
    effects.enter(types.lineEnding);
    effects.consume(code);
    effects.exit(types.lineEnding);
    return factorySpace(effects, afterPrefix, types.linePrefix);
  }
  function afterPrefix(code) {
    if (markdownLineEnding(code)) {
      return atLineEnding(code);
    }
    effects.enter(types.data);
    return comment2(code);
  }
  function commentClose(code) {
    if (code === codes.dash) {
      effects.consume(code);
      return end;
    }
    return comment2(code);
  }
  function end(code) {
    if (code === codes.greaterThan) {
      effects.exit(types.data);
      effects.enter("commentEnd");
      effects.consume(code);
      effects.exit("commentEnd");
      effects.exit("comment");
      return ok(code);
    }
    if (code === codes.dash) {
      effects.consume(code);
      return end;
    }
    return comment2(code);
  }
}
function mdxjs(options) {
  const settings = Object.assign(
    {
      acorn: Parser.extend(acornJsx()),
      acornOptions: { ecmaVersion: 2024, sourceType: "module" },
      addResult: true
    },
    options
  );
  return combineExtensions([
    mdxJsx(settings),
    mdxMd()
  ]);
}
const emptyOptions = {};
function remarkMdx(options) {
  const self = this;
  const settings = options || emptyOptions;
  const data = self.data();
  const micromarkExtensions = data.micromarkExtensions || (data.micromarkExtensions = []);
  const fromMarkdownExtensions = data.fromMarkdownExtensions || (data.fromMarkdownExtensions = []);
  micromarkExtensions.push(mdxjs(settings));
  fromMarkdownExtensions.push(mdxFromMarkdown());
}
function getMdxProcessor({
  plugins
}) {
  let processor = unified().use(remarkParse);
  plugins == null ? void 0 : plugins.forEach(({ mdxRemark }) => {
    if (mdxRemark)
      processor = mdxRemark(processor);
  });
  processor = processor.use(remarkMdx).use(remarkComment);
  return processor;
}
function remarkDisableFeature() {
  const self = this;
  const data = self.data();
  const micromarkExtensions = data.micromarkExtensions || (data.micromarkExtensions = []);
  micromarkExtensions.push({
    disable: { null: ["codeIndented", "blankLine"] }
  });
}
const remarkRehypeOptions = {};
function getProcessor({
  plugins,
  sanitize
}) {
  var _a;
  let processor = unified().use(remarkParse).use(remarkDisableFeature);
  plugins == null ? void 0 : plugins.forEach(({ remark }) => {
    if (remark)
      processor = remark(processor);
  });
  processor = processor.use(remarkRehype, { allowDangerousHtml: true, ...remarkRehypeOptions }).use(rehypeRaw);
  processor = processor.use(
    rehypeSanitize,
    (_a = sanitize == null ? void 0 : sanitize()) != null ? _a : defaultSchema
  );
  plugins == null ? void 0 : plugins.forEach(({ rehype }) => {
    if (rehype)
      processor = rehype(processor);
  });
  return processor.use(rehypeStringify);
}
function getErrorMessage(err) {
  return err instanceof Error ? err.message : JSON.stringify(err);
}
const schemaStr = JSON.stringify(defaultSchema);
function calcBytemdSafeMdxWorkerViewerValue({
  value,
  plugins,
  sanitizeFn,
  skipMdx
}) {
  let error = null;
  if (!skipMdx) {
    const mdxParser = getMdxProcessor({ plugins });
    try {
      const mdast = mdxParser.parse(value);
      return {
        mdast,
        error: null
      };
    } catch (e) {
      error = getErrorMessage(e);
    }
  }
  const SanitizeSchema = (() => {
    var _a;
    const nextSchema = JSON.parse(schemaStr);
    return (_a = sanitizeFn == null ? void 0 : sanitizeFn(nextSchema)) != null ? _a : nextSchema;
  })();
  const legacyParser = getProcessor({
    plugins,
    sanitize: () => SanitizeSchema
  });
  try {
    const _html = legacyParser.processSync(value).toString();
    return {
      _html,
      error
    };
  } catch (err) {
    error = getErrorMessage(err);
  }
  try {
    const _html = marked(value || "", {
      breaks: true,
      gfm: true,
      sanitize: false
    });
    return {
      _html,
      error
    };
  } catch (err) {
    error = getErrorMessage(err);
    return {
      _html: "",
      error
    };
  }
}
export {
  calcBytemdSafeMdxWorkerViewerValue,
  getMdxProcessor,
  getProcessor
};
